import React, { useEffect, useState } from "react";
import { memo } from "react";
import { useNavigate } from "react-router-dom";
import SalesTable from "./SalesTable/SalesTable";
import LoadingSpinner from "../../Components/Shared/Loader/Loader";
import axiosInstance from "../../Configuration/axiosInstance";
import SuccessDialogue from "../../Components/Shared/SuccessDialog/SuccessDialog";
import SearchStrip from "../../Components/SalesOrder/SearchStrip/SearchStrip";
import { generateBulkOrdersPDF } from "../../utils/pdfGenerator";

const SalesOrder = () => {
  const [orders, setOrders] = useState([]);
  const [custId, setCustID] = useState();
  const [customers, setCustomers] = useState();
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [changed, setDataChanged] = useState(false);
  const [date, setDate] = useState();
  const [count, setCount] = useState();
  const [clearFilters, setClearFilters] = useState(false);
  const [dialogBox, setDialogBox] = useState(false);
  const [msg, setMsg] = useState("");
  const [size, setSize] = useState(20);
  const [searchCriteria, setSearchCriteria] = useState({
    date: null,
    custId: null,
  });
  const [orderNumber, setOrderNumber] = useState();

  // ✅ NEW: State for tomorrow filter
  const [showTomorrowOnly, setShowTomorrowOnly] = useState(true);

  // ✅ NEW: State for incomplete orders summary
  const [incompleteOrdersCount, setIncompleteOrdersCount] = useState(0);
  const [ordersSummary, setOrdersSummary] = useState({
    pending: 0,
    assigned: 0,
    total: 0,
  });

  // ✅ NEW: Helper function to get tomorrow's date
  const getTomorrowDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split("T")[0]; // Returns YYYY-MM-DD format
  };

  const status = ["Pending", "Assigned", "Cancelled"];
  const tableHeaders = [
    "Order Number",
    "Customer Name",
    "Purchase Order No.",
    "Comments",
    "SKU's",
    "Order Date(dd/mm/yyyy)",
    "Order Type",
    "Order Status",
    "Images",
    "Action",
  ];

  // ✅ NEW: Fetch incomplete orders summary
  useEffect(() => {
    const fetchIncompleteOrdersSummary = async () => {
      try {
        // Fetch all incomplete orders (Pending + Assigned)
        const response = await axiosInstance.get(
          `order?status=Pending,Assigned&limit=1000`
        );
        const incompleteOrders = response.data.data || [];

        const summary = {
          pending: incompleteOrders.filter((order) => order.status === "Pending")
            .length,
          assigned: incompleteOrders.filter((order) => order.status === "Assigned")
            .length,
          total: incompleteOrders.length,
        };

        setOrdersSummary(summary);
        setIncompleteOrdersCount(summary.total);
      } catch (error) {
        console.error("Error fetching incomplete orders summary:", error);
      }
    };

    fetchIncompleteOrdersSummary();
  }, [changed]); // Refresh when orders change

  useEffect(() => {
    const fetchInitialData = async () => {
      setLoading(true);
      try {
        // ✅ NEW: Build query parameters based on filter state
        let queryParams = `page=${page}&limit=${pageSize}&customer=${searchCriteria.custId}&orderNumber=${searchCriteria.orderNumber}&status=${status}`;

        // Add date filter based on current state
        if (showTomorrowOnly) {
          queryParams += `&date=${getTomorrowDate()}`;
        } else if (searchCriteria.date) {
          queryParams += `&date=${searchCriteria.date}`;
        } else {
          queryParams += `&date=null`;
        }

        const [ordersResponse, customersResponse] = await Promise.all([
          axiosInstance.get(`order?${queryParams}`),
          axiosInstance.get(`customers/allcustomers`),
        ]);
        setOrders(ordersResponse.data.data);
        setCount(ordersResponse.data.count);
        setCustomers(customersResponse.data.data);
      } catch (err) {
        console.log(err);
      }
      setLoading(false);
    };

    fetchInitialData();
  }, [page, pageSize, changed, showTomorrowOnly, searchCriteria]);

  // ✅ NEW: Toggle between tomorrow and all orders
  const handleFilterToggle = (filterType) => {
    if (filterType === "tomorrow") {
      setShowTomorrowOnly(true);
      setDate(null);
      setSearchCriteria((prev) => ({ ...prev, date: null }));
    } else {
      setShowTomorrowOnly(false);
    }
    setPage(1); // Reset to first page when changing filters
  };

  // ✅ NEW: Clear all filters including tomorrow filter
  const handleClearAllFilters = () => {
    setShowTomorrowOnly(false);
    setDate(null);
    setCustID(null);
    setOrderNumber(null);
    setSearchCriteria({ date: null, custId: null });
    setClearFilters(!clearFilters);
    setPage(1);
  };

  const customerOptions = customers?.map((i) => ({
    value: i._id,
    label: i.businessName,
  }));

  const navigate = useNavigate();
  const updateOrder = (i) => {
    navigate("/salesOrders/updateOrder", { state: i });
  };

  const deleteOrder = async (i) => {
    try {
      axiosInstance
        .delete(`order/${i}`)
        .then((response) => {
          console.log(response.data.data);
          setDataChanged(!changed);
        })
        .catch((error) => {
          setDialogBox(true);
          setMsg(error.response.data.error);
          setTimeout(() => {
            setDialogBox(false);
          }, 1500);
          throw error;
        });
    } catch (error) {
      console.log("Caught an exception:", error);
    }
  };

  const handleNext = () => {
    setPage((preValue) => preValue + 1);
  };
  const handlePrevious = () => {
    setPage((preValue) => preValue - 1);
  };

  // Add this state for bulk PDF generation
  const [generatingBulkPDF, setGeneratingBulkPDF] = useState(false);

  // Add this function before the return statement
  const handleBulkPDFGenerate = async (orders) => {
    if (!orders || orders.length === 0) {
      alert('No orders to generate PDF for.');
      return;
    }

    if (!window.confirm(`Generate PDF for ${orders.length} orders? This may take a moment.`)) {
      return;
    }

    try {
      setGeneratingBulkPDF(true);
      
      // Simulate a small delay for UX
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      generateBulkOrdersPDF(orders);
      
      console.log(`Bulk PDF generated for ${orders.length} orders`);
    } catch (error) {
      console.error('Error generating bulk PDF:', error);
      alert('Failed to generate bulk PDF. Please try again.');
    } finally {
      setGeneratingBulkPDF(false);
    }
  };

  return (
    <div>
      <SuccessDialogue show={dialogBox} msg={msg} />
      <h2>All Sales Orders</h2>

      {/* ✅ NEW: Orders Summary Card */}
      <div
        style={{
          marginBottom: "20px",
          padding: "20px",
          backgroundColor: "#fff",
          border: "1px solid #dee2e6",
          borderRadius: "8px",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        }}
      >
        <h4
          style={{
            margin: "0 0 15px 0",
            color: "#495057",
          }}
        >
          📊 Orders Summary
        </h4>
        <div
          style={{
            display: "flex",
            gap: "20px",
            flexWrap: "wrap",
          }}
        >
          <div
            style={{
              padding: "15px",
              backgroundColor: "#fff3cd",
              border: "1px solid #ffeaa7",
              borderRadius: "6px",
              minWidth: "150px",
              textAlign: "center",
            }}
          >
            <div
              style={{
                fontSize: "24px",
                fontWeight: "bold",
                color: "#856404",
              }}
            >
              {ordersSummary.pending}
            </div>
            <div
              style={{
                fontSize: "14px",
                color: "#856404",
              }}
            >
              Pending Orders
            </div>
          </div>

          <div
            style={{
              padding: "15px",
              backgroundColor: "#d1ecf1",
              border: "1px solid #a6d8e8",
              borderRadius: "6px",
              minWidth: "150px",
              textAlign: "center",
            }}
          >
            <div
              style={{
                fontSize: "24px",
                fontWeight: "bold",
                color: "#0c5460",
              }}
            >
              {ordersSummary.assigned}
            </div>
            <div
              style={{
                fontSize: "14px",
                color: "#0c5460",
              }}
            >
              Assigned Orders
            </div>
          </div>

          <div
            style={{
              padding: "15px",
              backgroundColor: "#f8d7da",
              border: "1px solid #f1aeb5",
              borderRadius: "6px",
              minWidth: "150px",
              textAlign: "center",
            }}
          >
            <div
              style={{
                fontSize: "24px",
                fontWeight: "bold",
                color: "#721c24",
              }}
            >
              {incompleteOrdersCount}
            </div>
            <div
              style={{
                fontSize: "14px",
                color: "#721c24",
              }}
            >
              Total Incomplete
            </div>
          </div>

          <div
            style={{
              padding: "15px",
              backgroundColor: "#d4edda",
              border: "1px solid #a3d9a4",
              borderRadius: "6px",
              minWidth: "150px",
              textAlign: "center",
            }}
          >
            <div
              style={{
                fontSize: "14px",
                color: "#155724",
                fontWeight: "bold",
              }}
            >
              ⚠️ Requires Attention
            </div>
            <div
              style={{
                fontSize: "12px",
                color: "#155724",
              }}
            >
              {incompleteOrdersCount > 0
                ? `${incompleteOrdersCount} orders need completion`
                : "All orders up to date!"}
            </div>
          </div>
        </div>
      </div>

      {/* ✅ NEW: Filter Toggle Buttons */}
      <div
        style={{
          marginBottom: "20px",
          padding: "15px",
          backgroundColor: "#f8f9fa",
          borderRadius: "8px",
          display: "flex",
          gap: "10px",
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <span style={{ fontWeight: "bold", marginRight: "10px" }}>View:</span>
        <button
          onClick={() => handleFilterToggle("tomorrow")}
          style={{
            padding: "8px 16px",
            backgroundColor: showTomorrowOnly ? "#007bff" : "#6c757d",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
            fontWeight: showTomorrowOnly ? "bold" : "normal",
          }}
        >
          📅 Tomorrow's Orders ({getTomorrowDate()})
        </button>
        <button
          onClick={() => handleFilterToggle("all")}
          style={{
            padding: "8px 16px",
            backgroundColor: !showTomorrowOnly ? "#007bff" : "#6c757d",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
            fontWeight: !showTomorrowOnly ? "bold" : "normal",
          }}
        >
          📋 All Orders
        </button>
        {!showTomorrowOnly && (
          <button
            onClick={handleClearAllFilters}
            style={{
              padding: "8px 16px",
              backgroundColor: "#dc3545",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              marginLeft: "10px",
            }}
          >
            🗑️ Clear All Filters
          </button>
        )}
        <div
          style={{
            marginLeft: "auto",
            fontSize: "14px",
            color: "#6c757d",
          }}
        >
          {showTomorrowOnly
            ? `Showing ${count || 0} orders scheduled for tomorrow`
            : `Showing ${count || 0} orders total`}
        </div>
      </div>

      <SearchStrip
        size={size}
        setSize={setSize}
        setPage={setPage}
        setDate={setDate}
        setSearchCriteria={setSearchCriteria}
        date={date}
        custId={custId}
        clearFilters={clearFilters}
        setDataChanged={setDataChanged}
        setClearFilters={setClearFilters}
        setCustID={setCustID}
        changed={changed}
        customerOptions={customerOptions}
        searchCriteria={searchCriteria}
        customers={customers}
        setPageSize={setPageSize}
        pageSize={pageSize}
        orderNumber={orderNumber}
        setOrderNumber={setOrderNumber}
        // ✅ NEW: Pass tomorrow filter state to disable date picker when showing tomorrow's orders
        showTomorrowOnly={showTomorrowOnly}
      />

      {loading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "300px",
          }}
        >
          <LoadingSpinner />
        </div>
      ) : (
        <SalesTable
          orders={orders}
          setOrders={setOrders}
          updateOrder={updateOrder}
          deleteOrder={deleteOrder}
          handlePrevious={handlePrevious}
          handleNext={handleNext}
          page={page}
          pageSize={pageSize}
          count={count}
          tableHeaders={tableHeaders}
          showTomorrowOnly={showTomorrowOnly}
          onBulkPDFGenerate={handleBulkPDFGenerate} // ✅ NEW: Add this prop
        />
      )}
    </div>
  );
};

export default memo(SalesOrder);
